import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  discountsApi,
} from '../services/discountsApi';
import type {
  CreateGroupDiscountRequest,
  UpdateGroupDiscountRequest,
  CreateCustomerDiscountRequest,
  UpdateCustomerDiscountRequest,
} from '../services/discountsApi';

// Group Discounts
export const useGroupDiscounts = (groupId: string) => {
  return useQuery({
    queryKey: ['groupDiscounts', groupId],
    queryFn: () => discountsApi.getGroupDiscounts(groupId),
  });
};

export const useCreateGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (payload: CreateGroupDiscountRequest) =>
      discountsApi.createGroupDiscount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['groupDiscounts', groupId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useUpdateGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (data: { discountId: string; payload: UpdateGroupDiscountRequest }) =>
      discountsApi.updateGroupDiscount(data.discountId, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['groupDiscounts', groupId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useDeleteGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (discountId: string) =>
      discountsApi.deleteGroupDiscount(discountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['groupDiscounts', groupId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

// Maker-checker approval workflow for group campaigns
export const useSubmitGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (discountId: string) => discountsApi.submitGroupDiscount(discountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupDiscounts', groupId] });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useApproveGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (discountId: string) => discountsApi.approveGroupDiscount(discountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupDiscounts', groupId] });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useRejectGroupDiscount = (groupId: string) => {
  return useMutation({
    mutationFn: (data: { discountId: string; rejectionReason: string }) =>
      discountsApi.rejectGroupDiscount(data.discountId, data.rejectionReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupDiscounts', groupId] });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

// Customer Discounts
export const useCustomerDiscounts = (customerId: string) => {
  return useQuery({
    queryKey: ['customerDiscounts', customerId],
    queryFn: () => discountsApi.getCustomerDiscounts(customerId),
  });
};

export const useCreateCustomerDiscount = (customerId: string) => {
  return useMutation({
    mutationFn: (payload: CreateCustomerDiscountRequest) =>
      discountsApi.createCustomerDiscount(customerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customerDiscounts', customerId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useUpdateCustomerDiscount = (customerId: string) => {
  return useMutation({
    mutationFn: (data: { discountId: string; payload: UpdateCustomerDiscountRequest }) =>
      discountsApi.updateCustomerDiscount(data.discountId, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customerDiscounts', customerId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};

export const useDeleteCustomerDiscount = (customerId: string) => {
  return useMutation({
    mutationFn: (discountId: string) =>
      discountsApi.deleteCustomerDiscount(discountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customerDiscounts', customerId],
      });
      queryClient.invalidateQueries({ queryKey: ['effective-discount'] });
    },
  });
};